import {
  AccountType,
  UpdateInvestorDetailsMutationVariables,
  useInvestorDetailsDataQuery,
  useSectorsQuery,
  useUpdateInvestorDetailsMutation,
} from 'graphql/bindings/AdminBindings'
import {
  BooleanRadioButtonField,
  Box,
  FlagField,
  FormModal,
  FormSectionTitle,
  SpinnerButton,
  TextField,
  ValueAutocompleteField,
} from '@forgeglobal/design-framework'
import {
  CompanyStateOpts,
  InvestorStructureAbility,
  aumOptions,
  netWorth,
  opportunities,
  regions,
  targetCheckSize,
} from 'util/Enums'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { MutationResult } from 'react-apollo'
import { formatAbbreviatedMoney, formatPercent, parseAbbreviatedNumber, parsePercent } from 'util/NumberUtils'
import React from 'react'
import pick from 'lodash/pick'

interface InvPrefForm {
  target_check_size?: string
  has_cash_on_hand?: boolean
  investment_structure_comfort: string[]
  opportunities_of_interest: string[]
  sectors_interested: string[]
  sectors_avoid: string[]
  target_regions: string[]
  company_stages: string[]
  minimum_company_valuation?: number
  minimum_company_revenue?: number
  minimum_growth_rate?: number
  only_profitable_companies?: boolean
}

interface FormShape {
  id: string
  type?: AccountType
  net_worth?: string
  aum?: string
  investor_notes?: string
  investor_preferences: InvPrefForm
  mutationResult: MutationResult
}

const InvestorForm: React.FC<FormRenderProps<FormShape>> = ({ handleSubmit, form, values, valid }) => {
  const { data } = useSectorsQuery()
  const sectors = (data?.sectors ?? [])
    .map(sect => ({ label: sect.name, value: sect.name }))
    .sort((left: any, right: any) => (left.label > right.label ? 1 : -1))
  const sectorsAvoidOpts = sectors.map(sect => ({
    ...sect,
    isDisabled: values.investor_preferences.sectors_interested.includes(sect.value),
  }))
  const sectorsInterestedOpts = sectors.map(sect => ({
    ...sect,
    isDisabled: values.investor_preferences.sectors_avoid.includes(sect.value),
  }))
  const mutateInProgress = values.mutationResult.called
  return (
    <form onSubmit={handleSubmit}>
      <FormSectionTitle>Financial Information</FormSectionTitle>
      {values.type === 'individual' ? (
        <ValueAutocompleteField name="net_worth" label="Net Worth" options={netWorth} />
      ) : (
        <ValueAutocompleteField name="aum" label="AUM" options={aumOptions} />
      )}
      <ValueAutocompleteField
        name="investor_preferences.target_check_size"
        label="Target check size"
        options={targetCheckSize}
      />
      <Field
        name="investor_preferences.has_cash_on_hand"
        label="Has cash on hand"
        component={BooleanRadioButtonField}
      />
      <ValueAutocompleteField
        name="investor_preferences.investment_structure_comfort"
        label="Transaction structure ability"
        options={InvestorStructureAbility}
      />
      <FormSectionTitle>Interests</FormSectionTitle>
      <ValueAutocompleteField
        name="investor_preferences.opportunities_of_interest"
        label="Opportunities of interest"
        options={opportunities}
        multiple
      />
      <ValueAutocompleteField
        name="investor_preferences.sectors_interested"
        label="Sectors of interest"
        options={sectorsInterestedOpts}
        multiple
      />
      <ValueAutocompleteField
        name="investor_preferences.sectors_avoid"
        label="Sectors to avoid"
        options={sectorsAvoidOpts}
        multiple
      />
      <ValueAutocompleteField
        name="investor_preferences.target_regions"
        label="Target investment regions"
        options={regions.map(reg => ({ label: reg, value: reg }))}
        multiple
      />
      <FormSectionTitle>Company Preferences</FormSectionTitle>
      <ValueAutocompleteField
        name="investor_preferences.company_stages"
        label="Company stage"
        options={CompanyStateOpts}
        multiple
      />
      <Field
        name="investor_preferences.minimum_company_valuation"
        label="Valuation threshold"
        component={TextField}
        fullWidth
        parse={parseAbbreviatedNumber}
        format={formatAbbreviatedMoney}
      />
      <Field
        name="investor_preferences.minimum_company_revenue"
        label="Revenue threshold"
        component={TextField}
        fullWidth
        parse={parseAbbreviatedNumber}
        format={formatAbbreviatedMoney}
      />
      <Field
        name="investor_preferences.only_profitable_companies"
        label="Must be profitable?"
        component={FlagField}
        attached
        fullWidth
      />
      <Field
        name="investor_preferences.minimum_growth_rate"
        label="Growth rate"
        component={TextField}
        fullWidth
        parse={parsePercent}
        format={formatPercent}
      />
      <Field name="investor_notes" component={TextField} label="Additional information" fullWidth multiline />
      <Box mt={2}>
        <SpinnerButton
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          loading={mutateInProgress}
          caption="Save"
        />
      </Box>
    </form>
  )
}

export const AccountInvestorModal: React.FC<{ hideModal: () => void; accountId: string }> = ({
  hideModal,
  accountId,
}) => {
  const { data } = useInvestorDetailsDataQuery({ variables: { id: accountId } })
  const [mutate, mutateResult] = useUpdateInvestorDetailsMutation({
    onCompleted: hideModal,
  })
  const account = data?.account
  const investorPrefs = account?.investor_preferences
  const onSubmit = (values: FormShape) => {
    const investorFormPrefs = values?.investor_preferences
    const submitData: UpdateInvestorDetailsMutationVariables = {
      form: {
        ...pick(values, ['id', 'net_worth', 'aum', 'investor_notes']),
        investor_preferences: {
          ...pick(values.investor_preferences, [
            'target_check_size',
            'has_cash_on_hand',
            'minimum_company_valuation',
            'minimum_company_revenue',
            'minimum_growth_rate',
            'only_profitable_companies',
          ]),
          investment_structure_comfort: investorFormPrefs?.investment_structure_comfort ?? [],
          opportunities_of_interest: investorFormPrefs?.opportunities_of_interest ?? [],
          sectors_avoid: investorFormPrefs?.sectors_avoid ?? [],
          sectors_interested: investorFormPrefs?.sectors_interested ?? [],
          target_regions: investorFormPrefs?.target_regions ?? [],
          company_stages: investorFormPrefs?.company_stages ?? [],
        },
      },
    }
    mutate({ variables: submitData })
  }
  const initialValues: FormShape = {
    id: account?.id ?? '',
    type: account?.type,
    net_worth: account?.net_worth ?? undefined,
    aum: account?.aum ?? undefined,
    investor_preferences: {
      target_check_size: investorPrefs?.target_check_size ?? undefined,
      has_cash_on_hand: investorPrefs?.has_cash_on_hand ?? false,
      investment_structure_comfort: investorPrefs?.investment_structure_comfort ?? [],
      opportunities_of_interest: investorPrefs?.opportunities_of_interest ?? [],
      sectors_interested: investorPrefs?.sectors_interested ?? [],
      sectors_avoid: investorPrefs?.sectors_avoid ?? [],
      target_regions: investorPrefs?.target_regions ?? [],
      minimum_company_valuation: investorPrefs?.minimum_company_valuation ?? undefined,
      minimum_company_revenue: investorPrefs?.minimum_company_revenue ?? undefined,
      minimum_growth_rate: investorPrefs?.minimum_growth_rate ?? undefined,
      company_stages: investorPrefs?.company_stages ?? [],
      only_profitable_companies: investorPrefs?.only_profitable_companies ?? false,
    },
    investor_notes: account?.investor_notes ?? '',
    mutationResult: mutateResult,
  }
  return (
    <FormModal open hideModal={hideModal} title="Edit: Investor Details">
      <Form<FormShape>
        component={InvestorForm}
        onSubmit={onSubmit}
        initialValues={{
          ...initialValues,
        }}
      />
    </FormModal>
  )
}
